import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, CloudSun, Hourglass, Scissors, History, ListChecks } from "lucide-react";
import { Tile, StatusDot } from "../Primitives";
import PolicyRulesPanel from "./PolicyRulesPanel";
import SkuTable from "./SkuTable";
import ClassExplanation from "./ClassExplanation";
import { OriginBadge, syn } from "../../lib/origin.jsx";
import { ABCXYZ_COMPLIANCE } from "../../lib/dataExtras";
import { getInventoryDrill } from "../../lib/dataInventoryDetail";
import { formatPeso, formatNumber, cn } from "../../lib/utils";

function RulesApplied({ cell, zi, settings }) {
  const isZi = cell.code === "ZI";
  const abc = isZi ? null : cell.code[0];
  const items = [
    isZi ? [Hourglass, `${formatNumber(cell.count)} SKUs sold fewer than ${settings.slow_mover_units} units in 12 months and skip the variability formula`]
         : [CloudSun, `${formatNumber(cell.weather_in)} SKUs reclassified into ${cell.code} by weather`],
    isZi ? [ListChecks, `Value classes kept: A ${cell.abc_split.A} · B ${cell.abc_split.B} · C ${formatNumber(cell.abc_split.C)}`]
         : [Hourglass, `${formatNumber(zi.abc_split[abc])} ${abc}-class SKUs routed to Z-Intermittent`],
    [Scissors, `${formatNumber(cell.capped)} SKUs with capped outliers${cell.cap_changed ? ` (${cell.cap_changed} changed band)` : ""}`],
    [History, `${formatNumber(cell.legacy)} SKUs include legacy part history${cell.legacy_changed ? ` (${cell.legacy_changed} changed band)` : ""}`],
  ];
  return (
    <Tile title="Rules applied to this cell" testid="cell-rules-applied" dense>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
        {items.map(([Icon, text], i) => (
          <div key={i} data-testid={`cell-rule-applied-${i}`} className="bg-surface px-4 py-2.5 flex items-start gap-2 text-[12px]">
            <Icon size={13} className="text-primary mt-0.5 shrink-0" />{text}
          </div>
        ))}
      </div>
    </Tile>
  );
}

function Compliance({ items }) {
  return (
    <Tile title="Policy Compliance Breakdown" testid="cell-compliance" origin={syn("ABC × XYZ policy compliance")}>
      <div className="space-y-3">
        {items.map((m, i) => {
          const tone = m.value >= 0.85 ? "success" : m.value >= 0.65 ? "warning" : "critical";
          return (
            <div key={i} data-testid={`comp-metric-${i}`}>
              <div className="flex items-baseline justify-between mb-1">
                <div><span className="text-[12.5px] font-medium">{m.label}</span><span className="text-[10px] text-muted ml-2">{m.hint}</span></div>
                <span className={cn("font-mono-tabular text-sm font-semibold", `text-${tone}`)}>{(m.value * 100).toFixed(0)}% <span className="text-[10px] text-subtle font-normal">/ tgt {(m.target * 100).toFixed(0)}%</span></span>
              </div>
              <div className="relative h-2 bg-surface-2 rounded-sm overflow-hidden">
                <div className={cn("h-full", `bg-${tone}`)} style={{ width: `${m.value * 100}%` }} />
                <div className="absolute top-0 bottom-0 w-px bg-fg/70" style={{ left: `${m.target * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </Tile>
  );
}

function Narrative({ drill, code, branch }) {
  return (
    <>
      <ClassExplanation kind="cell" id={code} branch={branch} auto title="AI Explanation · why these SKUs sit here" className="tile p-4" testid="cell-ai" />
      {drill.nba?.length > 0 && (
        <Tile title="AI Recommendations · Next Best Actions" testid="cell-nba" origin={syn("next best actions")}>
          <div className="space-y-2">
            {drill.nba.map((a, i) => (
              <div key={i} data-testid={`cell-nba-${i}`} className="p-3 border border-border rounded-sm bg-surface-2/40 flex items-start gap-3">
                <div className={cn("w-2 h-2 rounded-full shrink-0 mt-1.5", a.urgency === "high" || a.urgency === "critical" ? "bg-critical" : a.urgency === "medium" ? "bg-warning" : "bg-info")} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-medium leading-snug">{a.title}</div>
                  <div className="text-[10px] text-muted mt-0.5">Owner: <span className="text-fg">{a.owner}</span> · Urgency: {a.urgency}</div>
                </div>
                <div className="text-right shrink-0 font-mono-tabular text-sm font-semibold text-success">{formatPeso(a.impact, { compact: true })}</div>
              </div>
            ))}
          </div>
        </Tile>
      )}
    </>
  );
}

// Wide drill-down for one ABC × XYZ cell (or the Z-Intermittent band): figures, rules applied, every SKU.
export default function CellDrillDrawer({ cell, summary, onClose }) {
  const branch = summary?.branch?.branch_id;
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!cell || !summary) return null;
  const zi = summary.cells.find(c => c.code === "ZI");
  const comp = ABCXYZ_COMPLIANCE[cell.code];
  const drill = getInventoryDrill("abcxyz", { ...cell, compliance: comp });
  const label = cell.code === "ZI" ? "Z-Intermittent / Slow-Moving" : `${cell.code} Cell`;
  return createPortal(
    <>
      <div data-testid="cell-drill-backdrop" onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in" />
      <aside data-testid="cell-drill-drawer"
             className="fixed top-0 right-0 h-screen w-full lg:w-[92vw] 2xl:w-[1320px] bg-bg border-l border-border shadow-2xl z-50 overflow-y-auto animate-slide-in-right">
        <div className="sticky top-0 bg-bg/95 backdrop-blur-md border-b border-border px-4 md:px-5 py-3.5 flex items-start justify-between gap-3 md:gap-4 z-20">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-1" data-testid="cell-drill-scope">
              Inventory Drill-Down · ABC × XYZ · {summary.branch ? `${summary.branch.name} branch` : "Network"}
            </div>
            <h2 className="font-display text-lg font-semibold tracking-tight leading-tight" data-testid="cell-drill-title">
              {label} · {formatNumber(cell.count)} SKUs · {formatPeso(cell.value, { compact: true })}
            </h2>
            <p className="text-[12px] text-muted mt-0.5">{cell.policy}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <OriginBadge {...summary.origin} testid="cell-drill-origin" />
            <button data-testid="cell-drill-close" onClick={onClose}
                    className="w-8 h-8 inline-flex items-center justify-center border border-border hover:border-primary hover:text-primary rounded-sm"><X size={14} /></button>
          </div>
        </div>
        <div className="p-3 md:p-5 space-y-4">
          <div className="tile grid grid-cols-2 md:grid-cols-5 divide-x divide-border" data-testid="cell-drill-kpis">
            {[["SKUs in cell", formatNumber(cell.count)], ["Annual value", formatPeso(cell.value, { compact: true })],
              [summary.branch ? "Share of branch value" : "Share of network value", `${(cell.share * 100).toFixed(1)}%`], ["Avg Risk Score", cell.avg_risk ?? "—"],
              ["Policy compliance (sample)", comp ? `${(comp.overall * 100).toFixed(0)}%` : "n/a", comp?.status]].map(([l, v, st]) => (
              <div key={l} className="px-3 py-2.5">
                <div className="kpi-label flex items-center gap-1.5 text-[10px]">{st && <StatusDot status={st} />}{l}</div>
                <div className="kpi-value text-lg mt-1">{v}</div>
              </div>
            ))}
          </div>
          <RulesApplied cell={cell} zi={zi} settings={summary.settings} />
          <PolicyRulesPanel rules={summary.rules} run={summary.run} testid="drill-policy-rules" />
          <SkuTable code={cell.code} branch={branch} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            {drill.compliance ? <Compliance items={drill.compliance} /> : <div />}
            <div className="space-y-4"><Narrative drill={drill} code={cell.code} branch={branch} /></div>
          </div>
        </div>
      </aside>
    </>,
    document.body,
  );
}
